import React from 'react';

/*
 * Stats, facts, and the compact status row.
 *
 * Numbers are set in IBM Plex Sans with tabular figures, the one face the
 * product uses, so counters line up without a second font. Stats sit on the card surface, never in a
 * bordered box: a row of four bordered boxes reads as four objects.
 */

type StatTone = 'default' | 'dark' | 'attention' | 'quiet';

const STAT_SHADOW = 'bg-white shadow-[0_1px_2px_rgba(24,25,29,0.06),0_4px_14px_rgba(24,25,29,0.06)]';

const STAT_TONE: Record<StatTone, string> = {
  default: STAT_SHADOW,
  dark: 'bg-black text-white',
  attention: STAT_SHADOW,
  quiet: 'bg-canvas-inset',
};

const STAT_LABEL: Record<StatTone, string> = {
  default: 'text-ink-muted',
  dark: 'text-white/70',
  attention: 'text-ink-muted',
  quiet: 'text-ink-muted',
};

const STAT_FOOT: Record<StatTone, string> = {
  default: 'text-ink-soft',
  dark: 'text-white/60',
  attention: 'text-ink-soft',
  quiet: 'text-ink-soft',
};

export interface StatProps {
  label: string;
  value: React.ReactNode;
  /** Which kind of number this is: reviewed, forecast, or not measured. */
  evidence?: React.ReactNode;
  tone?: StatTone;
  className?: string;
}

export const Stat: React.FC<StatProps> = ({ label, value, evidence, tone = 'default', className = '' }) => (
  <div className={`rounded-card px-4 py-4 sm:px-5 sm:py-5 ${STAT_TONE[tone]} ${className}`}>
    <div className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] ${STAT_LABEL[tone]}`}>
      {tone === 'attention' && <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-accent-500" />}
      {label}
    </div>
    <div className="mt-2 font-sans text-[28px] font-semibold leading-none tracking-[-0.02em] tabular-nums sm:text-[32px]">{value}</div>
    {evidence && <div className={`mt-1.5 text-[12px] ${STAT_FOOT[tone]}`}>{evidence}</div>}
  </div>
);

export interface Fact {
  label: string;
  value: React.ReactNode;
  right?: React.ReactNode;
}

/*
 * The compact status row: View / Last change / Access. Metadata, deliberately
 * not three oversized cards. Divided by a hairline, no surface of its own.
 */
export const CompactStatus: React.FC<{ facts: Fact[]; className?: string }> = ({ facts, className = '' }) => (
  <div className={`mb-5 flex flex-wrap items-stretch ${className}`}>
    {facts.map((f, i) => (
      <div
        key={f.label}
        className={`px-4 py-2.5 ${i < facts.length - 1 ? 'border-r border-line' : ''} ${i === 0 ? 'pl-0' : ''}`}
      >
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{f.label}</div>
        <div className="mt-0.5 flex items-center gap-2 text-[13px] font-semibold text-ink">
          {f.value}
          {f.right}
        </div>
      </div>
    ))}
  </div>
);

/*
 * The three-up fact strip inside a card (FOR / OWNER / TIME). Inset tiles on
 * the card they belong to, so they read as part of one object.
 */
export const FactStrip: React.FC<{ facts: Fact[]; className?: string }> = ({ facts, className = '' }) => (
  <div className={`grid gap-2 sm:grid-cols-3 ${className}`}>
    {facts.map((f) => (
      <div key={f.label} className="rounded-lg bg-white px-3.5 py-2.5">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{f.label}</div>
        <div className="mt-0.5 text-[13px] font-semibold text-ink">{f.value}</div>
      </div>
    ))}
  </div>
);

/** A key/value list: the "Current" facts panel in Company Context. */
export const FactList: React.FC<{ facts: Fact[]; className?: string }> = ({ facts, className = '' }) => (
  <div className={`divide-y divide-line-soft ${className}`}>
    {facts.map((f) => (
      <div key={f.label} className="flex items-center justify-between gap-4 px-6 py-3.5">
        <span className="shrink-0 text-[13px] text-ink-dim">{f.label}</span>
        <span className="flex min-w-0 items-center gap-3 text-right">
          <span className="truncate text-[13px] font-semibold text-ink">{f.value}</span>
          {f.right}
        </span>
      </div>
    ))}
  </div>
);
